import React, { useContext, useEffect, useState } from "react"
import readAsDataURL from "../utils/read-as-data-url"
import createPubSub from "../utils/create-pub-sub"
import { getEventRange, getEventTransfer } from "@convertkit/slate-react"
import { createCaptioned } from "./captioned"
import FileKit from "@convertkit/file-kit"

async function uploadToFileKit(file) {
  const filekit = FileKit({
    authURL: "/filekit/sessions",
    filekitURL: process.env.FILEKIT_HOST,
    // TODO: When refactoring this code out to the convertkit-editor repo,
    // find a way to pass this value in from the component instead of reading
    // it from window.
    id: window.app.user.account_id
  })

  return await filekit.upload(file)
}

const [subscribe, unsubscribe, publish] = createPubSub()

export const ImageDataContext = React.createContext({})

export function ImageData({ children }) {
  const [images, setImages] = useState({})

  useEffect(() => {
    const handler = newImages => {
      setImages({ ...images, ...newImages })
    }

    subscribe(handler)

    return () => {
      unsubscribe(handler)
    }
  })

  return (
    <ImageDataContext.Provider value={images}>
      {children}
    </ImageDataContext.Provider>
  )
}

export function useImageDataURL(key) {
  const context = useContext(ImageDataContext)
  return context[key]
}

export function disableImageOptimization(src) {
  if (!src.includes("filekitcdn.com")) return src

  const separator = src.includes("?") ? "&" : "?"
  return src + separator + "auto="
}

function Image({ node, attributes, optimize, isFocused }) {
  const src = node.data.get("src")
  const alt = node.data.get("alt")
  const dataURL = useImageDataURL(node.key)
  const attrs = isFocused ? { "data-focused": "" } : {}

  const srcWithOptions =
    src && optimize === false ? disableImageOptimization(src) : src

  return (
    <img
      alt={alt}
      src={srcWithOptions || dataURL}
      {...attrs}
      {...attributes}
      style={{ maxWidth: "100%" }}
    />
  )
}

export default () => {
  return {
    schema: {
      blocks: {
        image: {
          isVoid: true
        }
      }
    },
    commands: {
      insertImage: (editor, { caption, src, data }) => {
        editor.insertCaptioned({
          type: "image",
          caption,
          data: { src, ...data }
        })
      }
    },
    renderNode(props, editor, next) {
      const { node } = props
      switch (node.type) {
        case "image":
          return <Image {...props} />
        default:
          return next()
      }
    },
    onDrop: async (event, editor, next) => {
      const target = getEventRange(event, editor)
      if (!target && event.type === "drop") return next()

      const transfer = getEventTransfer(event)
      const { type, files } = transfer

      if (type === "files") {
        for (const file of files) {
          const [mime] = file.type.split("/")
          if (mime !== "image") continue

          const dataURL = await readAsDataURL(file)
          const captioned = createCaptioned({ type: "image", caption: "" })
          const image = captioned.nodes.first()

          publish({ [image.key]: dataURL })

          editor.insertBlock(captioned)

          const result = await uploadToFileKit(file)

          editor.setNodeByKey(image.key, {
            data: {
              src: result.email_url,
              borderRadius: 4
            }
          })
        }
        return
      }

      next()
    }
  }
}
